import { inject, Service } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Api, ApiTypes, EndpointDescriptor } from '@app/api';
import { PayloadForFoodbacks } from '@core/interfaces/payload-for-foodbacks.interface';
import { PayloadForWinnerStatistic } from '@core/interfaces/payload-for-winner-statistic.interface';
import { NotificationService } from '@core/providers/notification/notification.service';
import { TopLowPerformingQuestionsBackendResponse } from '@shared/components/overview/foodback-overview/top-low-performing-questions.interface';
import {
  AccountScores,
  QuestionFoodbacksFromBackend,
  VenueCategoryAverageEntry,
} from '@shared/interfaces/analytics.interface';
import { DynamicDateRange } from '@shared/interfaces/date-range.interface';
import { AnalyticsBackendService } from './analytics/analytics-backend.service';
import { toAccountStatementFoodbacksRequest } from './analytics/analytics-list-request';
import {
  PayloadForVenueGroupsStatistics,
  PayloadForVenueStatistics,
} from './analytics/analytics-params.interface';
import {
  AnalyticsEndpointPair,
  AnalyticsResult,
  PlainReadResult,
  SharedAnalyticsBackendService,
} from './shared-analytics-backend.service';

/** Reads this service adds on top of the shared set; both halves named so neither can drift. */
const ACCOUNT_ANALYTICS_ENDPOINTS = {
  scores: {
    account: Api.surveys.analytics.accounts.scores,
    venue: Api.surveys.analytics.venues.scores,
  },
  categoryAverages: {
    account: Api.surveys.analytics.accounts.categoryAverages,
    venue: Api.surveys.analytics.venues.categoryAverages,
  },
  statementsPerformance: {
    account: Api.surveys.analytics.accounts.statements.performance,
    venue: Api.surveys.analytics.venues.statements.performance,
  },
} as const satisfies Record<string, AnalyticsEndpointPair>;

@Service()
export class AccountAnalyticsBackendService extends SharedAnalyticsBackendService {
  private notificationService = inject(NotificationService);

  private defaultWinnerStatisticBody: Pick<PayloadForWinnerStatistic, 'page' | 'limit'> & {
    dateFrom: null;
    dateTo: null;
  } = {
    page: 1,
    limit: 10,
    dateFrom: null,
    dateTo: null,
  };

  getScoreboardVenuesList$(
    accountUuid: string,
    payload: PayloadForVenueStatistics
  ): Observable<Api.surveys.analytics.accounts.venueScoreboard.PostResult> {
    return this.api.post(Api.surveys.analytics.accounts.venueScoreboard, {
      pathParams: { accountUuid },
      body: payload,
    });
  }

  getScoreboardVenuesListExcel$(
    accountUuid: string,
    payload: PayloadForVenueStatistics
  ): Observable<void> {
    return this.api.post(Api.surveys.exports.accounts.spreadsheet.venueScoreboard, {
      pathParams: { accountUuid },
      body: payload,
    });
  }

  getScoreboardGroupList$(
    accountUuid: string,
    payload: PayloadForVenueGroupsStatistics
  ): Observable<Api.surveys.analytics.accounts.venueScoreboard.groups.PostResult> {
    return this.api.post(Api.surveys.analytics.accounts.venueScoreboard.groups, {
      pathParams: { accountUuid },
      body: payload,
    });
  }

  getCategoryAveragesForAccount$(
    accountUuid: string,
    dateRange: DynamicDateRange,
    venueUuids: string[]
  ): Observable<VenueCategoryAverageEntry[]> {
    return this.makeAnalyticsRequest$(
      ACCOUNT_ANALYTICS_ENDPOINTS.categoryAverages,
      accountUuid,
      dateRange,
      venueUuids
    );
  }

  getAccountScores$(accountUuid: string, dateRange: DynamicDateRange): Observable<AccountScores> {
    return this.makeAnalyticsRequest$(
      ACCOUNT_ANALYTICS_ENDPOINTS.scores,
      accountUuid,
      dateRange
    ).pipe(
      map(response => ({
        currentCount: response.currentCount.slice(),
        currentScores: response.currentScores.map(
          AnalyticsBackendService.mapScoreFromBackendToScore
        ),
        previousScores: response.previousScores.map(
          AnalyticsBackendService.mapScoreFromBackendToScore
        ),
      }))
    );
  }

  getAccountTopLowPerformingQuestions$(
    accountUuid: string,
    dateRange: DynamicDateRange,
    venueUuids: string[]
  ): Observable<TopLowPerformingQuestionsBackendResponse[]> {
    return this.makeAnalyticsRequest$(
      ACCOUNT_ANALYTICS_ENDPOINTS.statementsPerformance,
      accountUuid,
      dateRange,
      venueUuids
    );
  }

  getWinnerStatisticList$(
    accountUuid: string,
    restrictions?: PayloadForWinnerStatistic
  ): Observable<Api.surveys.rewards.accounts.PostResult> {
    const objToSend = restrictions || this.defaultWinnerStatisticBody;

    return this.api
      .post(Api.surveys.rewards.accounts, {
        pathParams: { accountUuid },
        body: objToSend,
      })
      .pipe(
        catchError((error): Observable<never> => {
          this.reportWinnerStatisticError(error);

          return throwError(() => error);
        })
      );
  }

  /** The error body carries the message under `error`; fall back to the generic copy when it doesn't. */
  private reportWinnerStatisticError(error: { error?: { errorMessage?: string } }): void {
    const message = error?.error?.errorMessage;

    if (message) {
      this.notificationService.errorSnackbar(message);
    } else {
      this.notificationService.errorSnackbarWithKey('SHARED.NOTIFICATIONS.ERROR_PROCESSING');
    }
  }

  /**
   * The account half of a filtered read. The date range travels in the body here rather than the
   * query, and the fourth argument names venues rather than venue groups — the endpoint ignores
   * `venueGroups` whenever `venues` is set.
   */
  protected override makeAnalyticsRequest$<E extends AnalyticsEndpointPair>(
    endpoints: E,
    uuid: string,
    dateRange: DynamicDateRange,
    venues?: string[],
    extraPathParams: Record<string, string> = {}
  ): Observable<AnalyticsResult<E>> {
    const { dateFrom, dateTo, precision } = this.getRequestParams(dateRange);

    const endpoint: EndpointDescriptor = endpoints.account;

    return this.api.post(endpoint, {
      pathParams: { accountUuid: uuid, ...extraPathParams },
      body: { dateFrom, dateTo, precision, venues },
    } as never) as Observable<AnalyticsResult<E>>;
  }

  protected override plainRead$<E extends AnalyticsEndpointPair>(
    endpoints: E,
    uuid: string,
    extraPathParams: Record<string, string> = {}
  ): Observable<PlainReadResult<E>> {
    const endpoint: EndpointDescriptor = endpoints.account;

    return this.api.get(endpoint, {
      pathParams: { accountUuid: uuid, ...extraPathParams },
    }) as Observable<PlainReadResult<E>>;
  }

  override getFoodbacksWithQuestion$(
    uuid: string,
    payload: PayloadForFoodbacks,
    questionUuid: string
  ): Observable<QuestionFoodbacksFromBackend> {
    return this.api
      .post(Api.surveys.analytics.accounts.statements.foodbacks.paged, {
        pathParams: { accountUuid: uuid, statement: questionUuid },
        ...toAccountStatementFoodbacksRequest(payload),
      })
      .pipe(map(response => ({ totalCount: response.meta.total, items: response.items })));
  }

  /** Account-only: there is no venue endpoint for the per-venue last-foodback dates. */
  getFoodbackLastDate$(
    accountUuid: string
  ): Observable<ApiTypes.LastFoodbackDatesResponseDto['dates']> {
    return this.api
      .get(Api.surveys.analytics.accounts.lastFoodbacksVenuesDates, {
        pathParams: { accountUuid },
      })
      .pipe(map(response => response.dates));
  }
}
